import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard, FolderTree, ClipboardList, FileBarChart, Settings,
  Plus, ChevronRight, ChevronDown, Sun, Moon, LogOut, Loader2,
  Building2, Layers, CircleDot, LandPlot, Waves, Trash2, Pencil,
  Check, X, Download, AlertTriangle, Shield, Search, MapPin, RefreshCw
} from 'lucide-react';
import { fetchSln, ASSET_TYPE_META, STATUS_META, THEMES } from './nexusHelpers';
import NexusElement from './NexusElement';

const VIEWS = { DASHBOARD: 'dashboard', PORTFOLIO: 'portfolio', ASSET: 'asset', ZONE: 'zone', ELEMENT: 'element' };

/* ── Status dot ── */
const StatusDot = ({ status, size = 'w-2.5 h-2.5' }) => {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return <div className={`${size} rounded-full flex-shrink-0`} style={{ background: meta.color }} title={meta.label} />;
};

/* ── Status badge ── */
const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: meta.bg, color: meta.color }}>
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
};

/* ── Asset type icon ── */
const AssetIcon = ({ type, size = 'w-5 h-5' }) => {
  const meta = ASSET_TYPE_META[type] || ASSET_TYPE_META.custom;
  const Icon = meta.icon;
  return <Icon className={size} style={{ color: meta.color }} />;
};

export const ServiceLifeNexus = ({ embedded = false }) => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('dd-sln-theme') || 'dark');
  const t = THEMES[theme];

  // Navigation state
  const [view, setView] = useState(VIEWS.DASHBOARD);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [zones, setZones] = useState([]);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: 'info' });

  // Sidebar
  const [sidebarExpanded, setSidebarExpanded] = useState({});
  const [creating, setCreating] = useState(null); // 'portfolio' | 'asset' | 'zone' | 'element'
  const [newName, setNewName] = useState('');
  const [newAssetType, setNewAssetType] = useState('building');
  const [newAssetLocation, setNewAssetLocation] = useState('');

  useEffect(() => { localStorage.setItem('dd-sln-theme', theme); }, [theme]);
  useEffect(() => { if (!embedded && !authLoading && !user) navigate('/auth/login?redirect=/engineering/servicelife-nexus'); }, [user, authLoading, navigate, embedded]);

  const toast = useCallback((text, type = 'info') => {
    setMsg({ text, type });
    if (type !== 'error') setTimeout(() => setMsg({ text: '', type: 'info' }), 4000);
  }, []);

  // ── Data Loading ──
  const loadDashboard = useCallback(async () => {
    try {
      const d = await fetchSln('/api/sln/dashboard');
      setDashboard(d);
    } catch (e) { if (e.message === 'AUTH') navigate('/auth/login'); }
  }, [navigate]);

  useEffect(() => { if (user) loadDashboard(); }, [user, loadDashboard]);

  const loadAssets = useCallback(async (pid) => {
    try { setAssets(await fetchSln(`/api/sln/portfolios/${pid}/assets`)); } catch { setAssets([]); }
  }, []);

  const loadZones = useCallback(async (aid) => {
    try { setZones(await fetchSln(`/api/sln/assets/${aid}/zones`)); } catch { setZones([]); }
  }, []);

  const refreshElement = useCallback(async (zoneId, elementId) => {
    if (zoneId) {
      const els = await fetchSln(`/api/sln/zones/${zoneId}/elements`).catch(() => []);
      setElements(els);
      if (elementId) {
        const fresh = els.find(e => e.id === elementId);
        if (fresh) setSelectedElement(fresh);
      }
    }
  }, []);

  const loadElements = useCallback(async (zid) => {
    try { setElements(await fetchSln(`/api/sln/zones/${zid}/elements`)); } catch { setElements([]); }
  }, []);

  // ── Navigation ──
  const goToDashboard = useCallback(() => {
    setView(VIEWS.DASHBOARD); setBreadcrumb([]); loadDashboard();
    setSelectedPortfolio(null); setSelectedAsset(null); setSelectedZone(null); setSelectedElement(null);
  }, [loadDashboard]);

  const goToPortfolio = useCallback((portfolio) => {
    setSelectedPortfolio(portfolio); setSelectedAsset(null); setSelectedZone(null); setSelectedElement(null);
    setView(VIEWS.PORTFOLIO); setBreadcrumb([{ label: portfolio.portfolio_name, action: () => goToPortfolio(portfolio) }]);
    loadAssets(portfolio.id);
  }, [loadAssets]);

  const goToAsset = useCallback((asset, portfolio) => {
    const p = portfolio || selectedPortfolio;
    setSelectedAsset(asset); setSelectedZone(null); setSelectedElement(null);
    setView(VIEWS.ASSET);
    setBreadcrumb([
      { label: p?.portfolio_name, action: () => goToPortfolio(p) },
      { label: asset.asset_name, action: () => goToAsset(asset, p) },
    ]);
    loadZones(asset.id);
  }, [selectedPortfolio, goToPortfolio, loadZones]);

  const goToZone = useCallback((zone, asset, portfolio) => {
    const a = asset || selectedAsset;
    const p = portfolio || selectedPortfolio;
    setSelectedZone(zone); setSelectedElement(null);
    setView(VIEWS.ZONE);
    setBreadcrumb([
      { label: p?.portfolio_name, action: () => goToPortfolio(p) },
      { label: a?.asset_name, action: () => goToAsset(a, p) },
      { label: zone.zone_name, action: () => goToZone(zone, a, p) },
    ]);
    loadElements(zone.id);
  }, [selectedAsset, selectedPortfolio, goToPortfolio, goToAsset, loadElements]);

  const goToElement = useCallback((element, zone, asset, portfolio) => {
    const z = zone || selectedZone;
    const a = asset || selectedAsset;
    const p = portfolio || selectedPortfolio;
    setSelectedElement(element);
    setView(VIEWS.ELEMENT);
    setBreadcrumb([
      { label: p?.portfolio_name, action: () => goToPortfolio(p) },
      { label: a?.asset_name, action: () => goToAsset(a, p) },
      { label: z?.zone_name, action: () => goToZone(z, a, p) },
      { label: element.element_name },
    ]);
  }, [selectedZone, selectedAsset, selectedPortfolio, goToPortfolio, goToAsset, goToZone]);

  // ── CRUD ──
  const createPortfolio = async () => {
    if (!newName.trim()) return;
    try {
      await fetchSln('/api/sln/portfolios', { method: 'POST', body: JSON.stringify({ portfolio_name: newName.trim() }) });
      toast('Portfolio created');
      loadDashboard();
      window.dispatchEvent(new CustomEvent('prioris-portfolios-changed'));
    } catch (e) { toast(e.message, 'error'); }
    setCreating(null); setNewName('');
  };

  const createAsset = async () => {
    if (!newName.trim() || !selectedPortfolio) return;
    try {
      // If location provided, fetch climate data first
      let envData = null;
      if (newAssetLocation.trim()) {
        try {
          envData = await fetchSln('/api/sln/climate-lookup', {
            method: 'POST', body: JSON.stringify({ location: newAssetLocation.trim() }),
          });
        } catch { /* climate fetch optional */ }
      }
      const body = { asset_name: newName.trim(), asset_type: newAssetType, location: newAssetLocation.trim() };
      if (envData) {
        body.env_conditions = {
          temperature_mean: envData.temperature_mean, temperature_std: envData.temperature_std,
          rh_mean: envData.rh_mean, rh_std: envData.rh_std, rh_min: envData.rh_min, rh_max: envData.rh_max,
          rh_distribution: envData.rh_distribution, co2_mean: envData.co2_mean, co2_std: envData.co2_std,
          tow: envData.tow, psr_by_direction: envData.psr_by_direction,
          wind_orientation: '', prob_driving_rain: 0,
          data_source: 'open-meteo', latitude: envData.latitude, longitude: envData.longitude,
        };
      }
      await fetchSln(`/api/sln/portfolios/${selectedPortfolio.id}/assets`, { method: 'POST', body: JSON.stringify(body) });
      toast('Asset created' + (envData ? ' with climate data' : ''));
      loadAssets(selectedPortfolio.id);
      loadDashboard();
      window.dispatchEvent(new CustomEvent('prioris-portfolios-changed'));
    } catch (e) { toast(e.message, 'error'); }
    setCreating(null); setNewName(''); setNewAssetLocation('');
  };

  const createZone = async () => {
    if (!newName.trim() || !selectedAsset) return;
    try {
      await fetchSln(`/api/sln/assets/${selectedAsset.id}/zones`, {
        method: 'POST', body: JSON.stringify({ zone_name: newName.trim() }),
      });
      toast('Zone created');
      loadZones(selectedAsset.id);
    } catch (e) { toast(e.message, 'error'); }
    setCreating(null); setNewName('');
  };

  const createElementAction = async () => {
    if (!newName.trim() || !selectedZone) return;
    try {
      await fetchSln(`/api/sln/zones/${selectedZone.id}/elements`, {
        method: 'POST', body: JSON.stringify({ element_name: newName.trim() }),
      });
      toast('Element created');
      loadElements(selectedZone.id);
    } catch (e) { toast(e.message, 'error'); }
    setCreating(null); setNewName('');
  };

  const deleteItem = async (type, id) => {
    const map = { portfolio: 'portfolios', asset: 'assets', zone: 'zones', element: 'elements' };
    try {
      await fetchSln(`/api/sln/${map[type]}/${id}`, { method: 'DELETE' });
      toast(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      if (type === 'portfolio') goToDashboard();
      else if (type === 'asset') loadAssets(selectedPortfolio?.id);
      else if (type === 'zone') loadZones(selectedAsset?.id);
      else if (type === 'element') loadElements(selectedZone?.id);
      loadDashboard();
    } catch (e) { toast(e.message, 'error'); }
  };

  const loadDemo = async () => {
    setLoading(true);
    try {
      await fetchSln('/api/sln/load-demo', { method: 'POST' });
      toast('Demo portfolio loaded successfully');
      await loadDashboard();
    } catch (e) { toast(e.message, 'error'); }
    setLoading(false);
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: t.accent }} /></div>;
  if (!user) return null;

  const trialDays = user.trial_expires_at ? Math.max(0, Math.ceil((new Date(user.trial_expires_at) - new Date()) / 86400000)) : 35;
  const isExpired = trialDays === 0;

  // ── Create inline form ──
  const CreateForm = ({ type, onSubmit }) => {
    // Suggestions: when adding a zone/element, hint at names already in use on this asset
    const suggestions = type === 'zone'    ? (zones || []).map(z => z.zone_name).filter(Boolean)
                      : type === 'element' ? (elements || []).map(e => e.element_name).filter(Boolean)
                      : [];
    const listId = `sln-suggest-${type}`;
    return (
    <div className="flex flex-col gap-2 px-3 py-2 rounded-lg" style={{ background: t.inputBg, border: `1px solid ${t.accent}` }}>
      <div className="flex items-center gap-2">
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={`${type} name`}
          list={suggestions.length > 0 ? listId : undefined}
          onKeyDown={e => { if (e.key === 'Enter') onSubmit(); if (e.key === 'Escape') setCreating(null); }}
          className="flex-1 text-sm bg-transparent outline-none" style={{ color: t.text }}
          autoFocus data-testid={`sln-create-${type}-input`} />
        {suggestions.length > 0 && (
          <datalist id={listId}>
            {suggestions.map((s, i) => <option key={i} value={s} />)}
          </datalist>
        )}
        {type === 'asset' && (
          <select value={newAssetType} onChange={e => setNewAssetType(e.target.value)}
            className="text-xs px-1.5 py-1 rounded" style={{ background: t.bg, color: t.textMuted, border: `1px solid ${t.inputBorder}` }}>
            {Object.entries(ASSET_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
        <button onClick={onSubmit} className="p-1 rounded hover:bg-green-500/20" data-testid={`sln-create-${type}-confirm`}><Check className="w-3.5 h-3.5" style={{ color: t.accent }} /></button>
        <button onClick={() => setCreating(null)} className="p-1 rounded hover:bg-red-500/20"><X className="w-3.5 h-3.5" style={{ color: t.danger }} /></button>
      </div>
      {(type === 'zone' || type === 'element') && suggestions.length > 0 && (
        <p className="text-[10px]" style={{ color: t.textDim }}>
          Tip: start typing to reuse an existing {type} name on this project, or type a new one.
        </p>
      )}
      {type === 'asset' && (
        <input value={newAssetLocation} onChange={e => setNewAssetLocation(e.target.value)}
          placeholder="Location (e.g. Hong Kong) — auto-fetches climate data"
          className="text-xs bg-transparent outline-none px-0.5 py-0.5" style={{ color: t.textMuted }}
          data-testid="sln-create-asset-location" />
      )}
    </div>
    );
  };

  // ── Sidebar ──
  const Sidebar = () => (
    <aside className="w-56 flex-shrink-0 h-full overflow-y-auto border-r flex flex-col"
      style={{ background: t.sidebar, borderColor: t.cardBorder }} data-testid="sln-sidebar">
      <div className="p-4 pb-2">
        <h1 className="text-sm font-bold tracking-wide" style={{ color: t.text }}>ServiceLife Nexus</h1>
        <p className="text-[10px] mt-0.5" style={{ color: t.textDim }}>
          {isExpired ? 'Trial expired' : `${trialDays}d trial remaining`}
        </p>
      </div>
      <nav className="flex-1 px-2 py-1 space-y-0.5 text-xs">
        <SidebarItem icon={LayoutDashboard} label="Dashboard" active={view === VIEWS.DASHBOARD} onClick={goToDashboard} testId="sln-nav-dashboard" />
        <div className="py-1.5">
          <div className="flex items-center justify-between px-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: t.textDim }}>Portfolios</span>
            {!isExpired && (
              <button onClick={() => { setCreating('portfolio'); setNewName(''); }} className="p-0.5 rounded hover:bg-white/5"
                data-testid="sln-add-portfolio"><Plus className="w-3 h-3" style={{ color: t.textDim }} /></button>
            )}
          </div>
          {creating === 'portfolio' && <div className="px-1 mb-1"><CreateForm type="portfolio" onSubmit={createPortfolio} /></div>}
          {dashboard?.portfolios?.map(p => (
            <PortfolioTreeItem key={p.id} portfolio={p} expanded={sidebarExpanded}
              onToggle={(id) => setSidebarExpanded(prev => ({ ...prev, [id]: !prev[id] }))}
              onSelect={goToPortfolio} onSelectAsset={(a) => goToAsset(a, p)}
              activeId={view === VIEWS.PORTFOLIO ? selectedPortfolio?.id : (view === VIEWS.ASSET ? selectedAsset?.id : selectedElement?.id)}
              t={t} />
          ))}
        </div>
        <SidebarItem icon={ClipboardList} label="Inspections" disabled testId="sln-nav-inspections" />
        <SidebarItem icon={FileBarChart} label="Reports" disabled testId="sln-nav-reports" />
        <SidebarItem icon={Settings} label="Settings" disabled testId="sln-nav-settings" />
      </nav>
      <div className="p-3 border-t" style={{ borderColor: t.cardBorder }}>
        <p className="text-[10px] truncate" style={{ color: t.textDim }}>{user.email}</p>
      </div>
    </aside>
  );

  const SidebarItem = ({ icon: Icon, label, active, onClick, disabled, testId }) => (
    <button onClick={onClick} disabled={disabled}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-colors"
      style={{ background: active ? t.accentBg : 'transparent', color: active ? t.accent : (disabled ? t.textDim : t.textMuted), opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      data-testid={testId}>
      <Icon className="w-4 h-4" />
      <span className="font-medium">{label}</span>
      {disabled && <span className="text-[8px] ml-auto px-1 py-0.5 rounded" style={{ background: t.inputBg, color: t.textDim }}>soon</span>}
    </button>
  );

  const PortfolioTreeItem = ({ portfolio: p, expanded, onToggle, onSelect, onSelectAsset, activeId, t }) => {
    const isOpen = expanded[p.id];
    return (
      <div>
        <div className="flex items-center group">
          <button onClick={() => onToggle(p.id)} className="p-1"><ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} style={{ color: t.textDim }} /></button>
          <button onClick={() => onSelect(p)} className="flex-1 flex items-center gap-1.5 px-1.5 py-1.5 rounded text-left truncate"
            style={{ color: activeId === p.id ? t.accent : t.textMuted }}>
            <FolderTree className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate text-xs">{p.portfolio_name}</span>
          </button>
        </div>
        {isOpen && p.assets?.map(a => (
          <button key={a.id} onClick={() => onSelectAsset(a)}
            className="w-full flex items-center gap-1.5 pl-8 pr-2 py-1 rounded text-left text-xs truncate"
            style={{ color: activeId === a.id ? t.accent : t.textDim }}>
            <AssetIcon type={a.asset_type} size="w-3 h-3" />
            <span className="truncate">{a.asset_name}</span>
          </button>
        ))}
      </div>
    );
  };

  // ── Breadcrumb ──
  const Breadcrumb = () => (
    <div className="flex items-center gap-1.5 text-xs overflow-x-auto" data-testid="sln-breadcrumb">
      <button onClick={goToDashboard} className="font-medium whitespace-nowrap" style={{ color: t.accent }}>Nexus</button>
      {breadcrumb.map((b, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3" style={{ color: t.textDim }} />
          {b.action ? (
            <button onClick={b.action} className="font-medium whitespace-nowrap hover:underline" style={{ color: i === breadcrumb.length - 1 ? t.text : t.textMuted }}>{b.label}</button>
          ) : (
            <span className="font-semibold whitespace-nowrap" style={{ color: t.text }}>{b.label}</span>
          )}
        </div>
      ))}
    </div>
  );

  // ── Dashboard View ──
  const DashboardView = () => {
    if (!dashboard) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;
    const { portfolios, usage, limits } = dashboard;
    const hasData = portfolios.length > 0;
    return (
      <div className="space-y-6" data-testid="sln-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={{ color: t.text }}>Portfolio Dashboard</h2>
            <p className="text-xs mt-0.5" style={{ color: t.textDim }}>Inspection-informed residual life management</p>
          </div>
          <div className="flex items-center gap-2 text-[10px]" style={{ color: t.textDim }}>
            <span>{usage.portfolios}/{limits.portfolios} portfolios</span>
            <span className="w-px h-3" style={{ background: t.cardBorder }} />
            <span>{usage.assets}/{limits.assets} assets</span>
            <span className="w-px h-3" style={{ background: t.cardBorder }} />
            <span>{usage.elements}/{limits.elements} elements</span>
          </div>
        </div>

        {!hasData && (
          <div className="rounded-xl border-2 border-dashed p-12 text-center" style={{ borderColor: t.cardBorder }}>
            <FolderTree className="w-12 h-12 mx-auto mb-4 opacity-30" style={{ color: t.textDim }} />
            <h3 className="text-base font-semibold mb-2" style={{ color: t.text }}>Welcome to ServiceLife Nexus</h3>
            <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: t.textDim }}>
              Create a portfolio to start managing your infrastructure assets, or load the demo to explore the platform.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => { setCreating('portfolio'); setNewName(''); }}
                className="text-sm px-5" style={{ background: t.accent, color: '#fff' }}
                data-testid="sln-create-first-portfolio">
                <Plus className="w-4 h-4 mr-1.5" /> New Portfolio
              </Button>
              <Button variant="outline" onClick={loadDemo} disabled={loading}
                className="text-sm px-5" style={{ borderColor: t.inputBorder, color: t.textMuted }}
                data-testid="sln-load-demo-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                Load Demo
              </Button>
            </div>
            {creating === 'portfolio' && <div className="mt-4 max-w-xs mx-auto"><CreateForm type="portfolio" onSubmit={createPortfolio} /></div>}
          </div>
        )}

        {hasData && portfolios.map(p => (
          <div key={p.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <button onClick={() => goToPortfolio(p)} className="flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: t.text }} data-testid={`sln-portfolio-${p.id}`}>
                <FolderTree className="w-4 h-4" style={{ color: t.accent }} />
                {p.portfolio_name}
              </button>
              <span className="text-[10px]" style={{ color: t.textDim }}>{p.total_elements} elements across {p.assets?.length || 0} assets</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {p.assets?.map(a => (
                <AssetCard key={a.id} asset={a} t={t}
                  onClick={() => goToAsset(a, p)} />
              ))}
            </div>
          </div>
        ))}

        {/* Risk-ranked elements */}
        {hasData && <RiskTable dashboard={dashboard} t={t} onElementClick={(el) => {
          // Find the portfolio/asset/zone for this element and navigate
          for (const p of dashboard.portfolios) {
            for (const a of p.assets) {
              // We need to look deeper - just navigate to asset for now
            }
          }
        }} />}
      </div>
    );
  };

  const AssetCard = ({ asset: a, t, onClick }) => {
    const meta = ASSET_TYPE_META[a.asset_type] || ASSET_TYPE_META.custom;
    return (
      <button onClick={onClick}
        className="w-full text-left rounded-lg border p-4 transition-all hover:shadow-md group"
        style={{ background: t.card, borderColor: t.cardBorder }}
        data-testid={`sln-asset-card-${a.id}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}15` }}>
              <AssetIcon type={a.asset_type} size="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight" style={{ color: t.text }}>{a.asset_name}</p>
              <p className="text-[10px] mt-0.5" style={{ color: t.textDim }}>{meta.label}{a.location ? ` \u2014 ${a.location}` : ''}</p>
            </div>
          </div>
          <StatusBadge status={a.worst_status} />
        </div>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: t.textDim }}>
          <span>{a.zone_count} zones</span>
          <span>{a.element_count} elements</span>
          {a.min_beta != null && <span>{"β"}<sub>min</sub> = {a.min_beta}</span>}
          {a.status_counts && (
            <div className="flex items-center gap-1 ml-auto">
              {a.status_counts.ok > 0 && <span className="flex items-center gap-0.5"><StatusDot status="ok" size="w-1.5 h-1.5" />{a.status_counts.ok}</span>}
              {a.status_counts.monitor > 0 && <span className="flex items-center gap-0.5"><StatusDot status="monitor" size="w-1.5 h-1.5" />{a.status_counts.monitor}</span>}
              {a.status_counts.act > 0 && <span className="flex items-center gap-0.5"><StatusDot status="act" size="w-1.5 h-1.5" />{a.status_counts.act}</span>}
            </div>
          )}
        </div>
      </button>
    );
  };

  // ── Risk Table ──
  const RiskTable = ({ dashboard: d, t }) => {
    const allElements = [];
    for (const p of d.portfolios) {
      for (const a of (p.assets || [])) {
        // We don't have element-level data in dashboard, so skip if not available
      }
    }
    return null; // Will be populated when element-level data is available in dashboard
  };

  // ── Portfolio Detail ──
  const PortfolioView = () => {
    if (!selectedPortfolio) return null;
    return (
      <div className="space-y-5" data-testid="sln-portfolio-view">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ color: t.text }}>{selectedPortfolio.portfolio_name}</h2>
            <p className="text-xs" style={{ color: t.textDim }}>{selectedPortfolio.description || 'Portfolio'}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isExpired && (
              <Button size="sm" onClick={() => { setCreating('asset'); setNewName(''); }}
                style={{ background: t.accent, color: '#fff' }} data-testid="sln-add-asset">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Asset
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => deleteItem('portfolio', selectedPortfolio.id)}
              style={{ color: t.danger }} data-testid="sln-delete-portfolio">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {creating === 'asset' && <CreateForm type="asset" onSubmit={createAsset} />}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {assets.map(a => (
            <AssetCard key={a.id} asset={a} t={t} onClick={() => goToAsset(a)} />
          ))}
        </div>
        {assets.length === 0 && (
          <div className="text-center py-12 rounded-lg border-2 border-dashed" style={{ borderColor: t.cardBorder }}>
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
            <p className="text-sm" style={{ color: t.textDim }}>No assets yet. Add your first asset.</p>
          </div>
        )}
      </div>
    );
  };

  // ── Asset Detail (zones list) ──
  const AssetView = () => {
    const [editingLocation, setEditingLocation] = useState(false);
    const [locVal, setLocVal] = useState(selectedAsset?.location || '');
    const [savingLoc, setSavingLoc] = useState(false);
    const [fetchingClimate, setFetchingClimate] = useState(false);

    if (!selectedAsset) return null;

    const saveLocation = async () => {
      setSavingLoc(true);
      try {
        await fetchSln(`/api/sln/assets/${selectedAsset.id}`, {
          method: 'PATCH', body: JSON.stringify({ location: locVal.trim() }),
        });
        setEditingLocation(false);
        loadDashboard();
        // Update selectedAsset locally
        selectedAsset.location = locVal.trim();
      } catch (e) { toast(e.message, 'error'); }
      setSavingLoc(false);
    };

    const fetchClimateAndSave = async () => {
      if (!locVal.trim()) return;
      setFetchingClimate(true);
      try {
        const data = await fetchSln('/api/sln/climate-lookup', {
          method: 'POST', body: JSON.stringify({ location: locVal.trim() }),
        });
        const envUpdate = {
          temperature_mean: data.temperature_mean, temperature_std: data.temperature_std,
          rh_mean: data.rh_mean, rh_std: data.rh_std, rh_min: data.rh_min, rh_max: data.rh_max,
          rh_distribution: data.rh_distribution || 'beta',
          co2_mean: data.co2_mean, co2_std: data.co2_std,
          tow: data.tow, psr_by_direction: data.psr_by_direction,
          wind_orientation: '', prob_driving_rain: 0,
          data_source: 'open-meteo', latitude: data.latitude, longitude: data.longitude,
        };
        await fetchSln(`/api/sln/assets/${selectedAsset.id}`, {
          method: 'PATCH', body: JSON.stringify({ location: locVal.trim(), env_conditions: envUpdate }),
        });
        toast('Location & climate data updated');
        setEditingLocation(false);
        loadDashboard();
        selectedAsset.location = locVal.trim();
        selectedAsset.env_conditions = envUpdate;
      } catch (e) { toast(e.message, 'error'); }
      setFetchingClimate(false);
    };

    return (
      <div className="space-y-5" data-testid="sln-asset-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AssetIcon type={selectedAsset.asset_type} size="w-6 h-6" />
            <div>
              <h2 className="text-lg font-bold" style={{ color: t.text }}>{selectedAsset.asset_name}</h2>
              <p className="text-xs" style={{ color: t.textDim }}>
                {ASSET_TYPE_META[selectedAsset.asset_type]?.label}
                {selectedAsset.commissioning_year ? ` (${selectedAsset.commissioning_year})` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isExpired && (
              <Button size="sm" onClick={() => { setCreating('zone'); setNewName(''); }}
                style={{ background: t.accent, color: '#fff' }} data-testid="sln-add-zone">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Zone
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => deleteItem('asset', selectedAsset.id)}
              style={{ color: t.danger }}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {/* Asset Location field */}
        <div className="flex items-center gap-3 rounded-lg px-4 py-2.5" style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}>
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: t.textDim }} />
          {editingLocation ? (
            <div className="flex flex-1 items-center gap-2">
              <input value={locVal} onChange={e => setLocVal(e.target.value)}
                placeholder="e.g. Hong Kong, Lisbon, Singapore"
                className="flex-1 text-sm bg-transparent outline-none" style={{ color: t.text }}
                autoFocus onKeyDown={e => { if (e.key === 'Escape') setEditingLocation(false); }}
                data-testid="sln-asset-location-input" />
              <Button size="sm" onClick={fetchClimateAndSave} disabled={fetchingClimate || !locVal.trim()}
                style={{ background: t.accent, color: '#fff', fontSize: '10px' }} data-testid="sln-asset-fetch-climate">
                {fetchingClimate ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                <span className="ml-1">Save & Fetch Climate</span>
              </Button>
              <Button size="sm" variant="ghost" onClick={saveLocation} disabled={savingLoc}
                style={{ color: t.textMuted, fontSize: '10px' }} data-testid="sln-asset-save-location">
                {savingLoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                <span className="ml-1">Save only</span>
              </Button>
              <button onClick={() => setEditingLocation(false)} className="p-1"><X className="w-3 h-3" style={{ color: t.textDim }} /></button>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-between">
              <span className="text-sm" style={{ color: selectedAsset.location ? t.text : t.textDim }}>
                {selectedAsset.location || 'No location set — click to add'}
              </span>
              {!isExpired && (
                <button onClick={() => { setLocVal(selectedAsset.location || ''); setEditingLocation(true); }}
                  className="p-1 rounded hover:opacity-70" style={{ color: t.accent }} data-testid="sln-asset-edit-location">
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
        {creating === 'zone' && <CreateForm type="zone" onSubmit={createZone} />}
        <div className="space-y-2">
          {zones.map(z => (
            <button key={z.id} onClick={() => goToZone(z)}
              className="w-full text-left flex items-center justify-between rounded-lg border px-4 py-3 transition-colors group"
              style={{ background: t.card, borderColor: t.cardBorder }}
              data-testid={`sln-zone-${z.id}`}>
              <div className="flex items-center gap-3">
                <StatusDot status={z.worst_status} />
                <div>
                  <p className="text-sm font-medium" style={{ color: t.text }}>{z.zone_name}</p>
                  <p className="text-[10px]" style={{ color: t.textDim }}>{z.element_count} elements</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: t.textDim }} />
              </div>
            </button>
          ))}
          {zones.length === 0 && (
            <div className="text-center py-10 rounded-lg border-2 border-dashed" style={{ borderColor: t.cardBorder }}>
              <p className="text-sm" style={{ color: t.textDim }}>No zones. Add your first zone to this asset.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Zone Detail (elements list) ──
  const ZoneView = () => {
    if (!selectedZone) return null;
    return (
      <div className="space-y-5" data-testid="sln-zone-view">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ color: t.text }}>{selectedZone.zone_name}</h2>
            <p className="text-xs" style={{ color: t.textDim }}>Zone in {selectedAsset?.asset_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isExpired && (
              <Button size="sm" onClick={() => { setCreating('element'); setNewName(''); }}
                style={{ background: t.accent, color: '#fff' }} data-testid="sln-add-element">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Element
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => deleteItem('zone', selectedZone.id)}
              style={{ color: t.danger }}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        {creating === 'element' && <CreateForm type="element" onSubmit={createElementAction} />}
        <div className="space-y-2">
          {elements.map(el => (
            <button key={el.id} onClick={() => goToElement(el)}
              className="w-full text-left flex items-center justify-between rounded-lg border px-4 py-3 transition-colors group"
              style={{ background: t.card, borderColor: t.cardBorder }}
              data-testid={`sln-element-${el.id}`}>
              <div className="flex items-center gap-3">
                <StatusDot status={el.status} />
                <div>
                  <p className="text-sm font-medium" style={{ color: t.text }}>{el.element_name}</p>
                  <p className="text-[10px] font-mono" style={{ color: t.textDim }}>{el.uid}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: t.inputBg, color: t.textMuted }}>{el.exposure_class}</span>
                {el.current_beta != null && <span className="text-xs font-semibold" style={{ color: STATUS_META[el.status]?.color }}>{"β"} {el.current_beta}</span>}
                <ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100" style={{ color: t.textDim }} />
              </div>
            </button>
          ))}
          {elements.length === 0 && (
            <div className="text-center py-10 rounded-lg border-2 border-dashed" style={{ borderColor: t.cardBorder }}>
              <p className="text-sm" style={{ color: t.textDim }}>No elements. Add your first element to this zone.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── EMBEDDED MODE: no sidebar, no topbar, just content ──
  if (embedded) {
    return (
      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {/* Breadcrumb */}
        <div className="px-6 pt-4 pb-2"><Breadcrumb /></div>
        <div className="px-6 py-3">
          {view === VIEWS.DASHBOARD && <DashboardView />}
          {view === VIEWS.PORTFOLIO && <PortfolioView />}
          {view === VIEWS.ASSET && <AssetView />}
          {view === VIEWS.ZONE && <ZoneView />}
          {view === VIEWS.ELEMENT && selectedElement && (
            <NexusElement element={selectedElement} asset={selectedAsset} zone={selectedZone}
              t={t} isExpired={isExpired}
              onRefresh={() => refreshElement(selectedZone?.id, selectedElement?.id)}
              onBack={() => goToZone(selectedZone)} />
          )}
        </div>
        {msg.text && (
          <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-xl text-sm font-medium"
            style={{ background: msg.type === 'error' ? t.danger : t.accent, color: '#fff' }}>
            {msg.text}
            <button onClick={() => setMsg({ text: '', type: 'info' })} className="ml-2 text-white/70 hover:text-white">&times;</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: t.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0"
        style={{ background: t.bar, borderColor: t.cardBorder }} data-testid="sln-topbar">
        <Breadcrumb />
        <div className="flex items-center gap-2">
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-1.5 rounded-md" style={{ background: t.inputBg }}
            data-testid="sln-theme-toggle">
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} /> : <Moon className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />}
          </button>
          <Button variant="ghost" size="sm" onClick={logout} style={{ color: t.textMuted }} data-testid="sln-logout">
            <LogOut className="w-3.5 h-3.5" />
          </Button>
        </div>
      </header>

      {/* Trial expired banner */}
      {isExpired && (
        <div className="flex items-center justify-between px-4 py-2 text-xs" style={{ background: t.dangerBg, borderBottom: `1px solid ${t.danger}30` }} data-testid="sln-trial-banner">
          <div className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" style={{ color: t.danger }} /><span style={{ color: t.danger }}>Trial expired — read-only mode. Upgrade to continue editing.</span></div>
          <Button size="sm" className="text-xs h-6 px-3" style={{ background: t.danger, color: '#fff' }}>Upgrade</Button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto px-6 py-5">
          {view === VIEWS.DASHBOARD && <DashboardView />}
          {view === VIEWS.PORTFOLIO && <PortfolioView />}
          {view === VIEWS.ASSET && <AssetView />}
          {view === VIEWS.ZONE && <ZoneView />}
          {view === VIEWS.ELEMENT && selectedElement && (
            <NexusElement element={selectedElement} asset={selectedAsset} zone={selectedZone}
              t={t} isExpired={isExpired}
              onRefresh={() => refreshElement(selectedZone?.id, selectedElement?.id)}
              onBack={() => goToZone(selectedZone)} />
          )}
        </main>
      </div>

      {/* Toast */}
      {msg.text && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-xl text-sm font-medium"
          style={{ background: msg.type === 'error' ? t.danger : t.accent, color: '#fff' }}
          data-testid="sln-toast">
          {msg.text}
          <button onClick={() => setMsg({ text: '', type: 'info' })} className="ml-2 text-white/70 hover:text-white">&times;</button>
        </div>
      )}
    </div>
  );
};

export default ServiceLifeNexus;
